import { HTMLTable } from "./HTMLTable";
import { IHeaderCol, ICoreFrameGridOptions, generatorFunction, totalFunction, IColSizes, ITableInformation } from "../Interfaces";
import { CoreFrameGrid } from "../index";
import { Constants } from "../Constants";
import { Utilities } from "../Utilities";
import { ColumnWidthHelper } from "../Components/ColumnWidthHelper";
import { ColumnResizer } from "../ColumnResizer";
import type { HeaderTable } from "./HTMLHeaderTable";

export class BodyTable extends HTMLTable {

    tbody: HTMLElement = undefined;
    private allCells: any[][] = [];
    private columnResizer: ColumnResizer;
    
    public $tbody: JQuery;

    constructor(options: ICoreFrameGridOptions, grid: CoreFrameGrid) {
        super('GridBodyTable', options, grid);

        
        this.tbody = document.createElement('tbody');
        this.tbody.setAttribute('role', 'rowgroup');
        this.tbody.setAttribute('tabindex', '-1');
        this.$table[0].appendChild(this.tbody);
        this.$wrapper[0].style.overflowX = "auto";
        this.$wrapper[0].style.overflowY = "scroll";
        this.$wrapper[0].setAttribute('tabindex', '0');
        this.columnResizer = new ColumnResizer(this.grid);
        this.$tbody = $(this.tbody);
    }

    /**
     * Verknüpft die Spaltenköpfe der Header-Tabelle per aria-owns mit diesem Body-Grid (role="grid").
     */
    public connectHeader(header: HeaderTable): void {
        this.$table.attr('aria-owns', header.getHeaderRowGroupId());
    }

    public getScrollbarThickness(): any {
        // Hier muss mit einem unabängigen Element gearbeitet werden, da die Tabelle noch nicht gerendet wurde
        const $outer = $('<div>').css({ visibility: 'hidden', width: '100px', overflow: 'scroll' }).appendTo('body'),
            widthWithScroll = $('<div>').css({ width: '100%' }).appendTo($outer).outerWidth();
        $outer.remove();
        return 100 - widthWithScroll;
    }

    /**
     * Entfernt alle Zeilen aus dem DOM und ruft für jede Zeile das Callback "onBeforeRowRemoved" auf
     */
    private clearBody(){
        let trs = this.tbody.getElementsByTagName('tr');
        for (let i = trs.length-1; i >= 0 ; i--) {
            const item = trs[i];
            this.grid.onBeforeRowRemoved($(item) as JQuery<HTMLTableRowElement>);
            item.remove();
        }
    }

    public prepareBodySync(rows: Array<any>): ITableInformation {
        this.allCells = [];
        this.clearBody();

        if (!rows) {
            return;
        }
        
        const trs = [];
        const cols = this.grid.getAllVisibleLeafColumns();
        const cellValueSizes: number[][] = [];

        const editMode = typeof this.options.isEditable != "boolean" && this.options.isEditable.isInstantEdit;
        for (let i = 0; i < rows.length; i++) {
            const $row = this.createDataRowSync(rows[i], i, editMode, cellValueSizes);
            trs.push($row);
        }

        const calculated: number[] = [];
        const finalRows = this.finalizeBodyTable(trs, cols, cellValueSizes, calculated);

        const tableInfo: ITableInformation = {
            calculatedWidths: calculated,
            rows: finalRows
        }

        return tableInfo;
    }

    public async prepareBodyAsync(rows: Array<any>) {
        return new Promise<ITableInformation>((resolve) => {
            setTimeout(async () => {
                this.allCells = [];
                this.clearBody();
        
                if (!rows) {
                    return;
                }
                
                const trs = [];
                const cols = this.grid.getAllVisibleLeafColumns();
                const cellValueSizes: number[][] = [];

                const editMode = typeof this.options.isEditable != "boolean" && this.options.isEditable.isInstantEdit;
                const rowAmount = rows.length;
                const chunkingSize = 50;

                if (rowAmount > chunkingSize) {
                    for (let i = 0; i < rows.length; i += chunkingSize) {
                        const buildedRows = await this.createMultipleDataRowsAsync(rows, i, editMode, cellValueSizes, chunkingSize);
                        trs.push(buildedRows);
                    }
                } else {
                    for (let i = 0; i < rows.length; i++) {
                        const $row = await this.createDataRowAsync(rows[i], i, editMode, cellValueSizes);
                        trs.push($row);
                    }
                }

                
                const calculated: number[] = [];
                const finalRows = await this.finalizeBodyTableAsync(trs.flat(), cols, cellValueSizes, calculated);
        
                const tableInfo: ITableInformation = {
                    calculatedWidths: calculated,
                    rows: finalRows
                }

                resolve(tableInfo)
            })
        });
    }

    private async finalizeBodyTableAsync(rows: Array<any>, cols: Array<IHeaderCol>, cellValueSizes: number[][], calculatedWidths: number[]): Promise<Array<HTMLTableRowElement>> {
        return new Promise<Array<HTMLTableRowElement>>((resolve) => {
            setTimeout(() => {
                resolve(this.makeTableRows(rows, cols, cellValueSizes, calculatedWidths));
            })
        })
    }

    private makeTableRows(rows: Array<any>, cols: Array<IHeaderCol>, cellValueSizes: number[][], calculatedWidths: number[]): Array<HTMLTableRowElement> {
        this.grid.refreshTotalRow();

        let sizesForColumns: number[] = [];
        const trs = rows;
        const save: number[][] = [];
        const base: [] = [];
        for (let i = 0; i < cols.length; i++) {
            save.push(base.slice());
        }
        
        for (let i = 0; i < cellValueSizes.length; i++) {
            for (let j = 0; j < cellValueSizes[i].length; j++) {
                save[j].push(cellValueSizes[i][j]);
            }
        }

        // Auswerten der dargestellten Grid-Größe
        const gridTable = this.grid.getBody().$table[0];
        const gridWidth = gridTable.getBoundingClientRect().width;
        const parentWidth = gridTable.parentElement.getBoundingClientRect().width

        // im Vorfeld alle AVG und MAX-Werte bestimmen
        const baseTR = trs[0];
        const colSizes = this.getMaxAndAverage(save, cols);

        sizesForColumns = colSizes.maxColSizes;
        const sum = Utilities.sum(colSizes.maxColSizes);

        // ist das dargestellte Grid bereits in einem Overflow (z.B. viele dargestellte Spalten) ODER sind die ermittelten MAX-Werte aufsummiert größer als das dargestellte Grid => Nehme die AVG-Werte
        if (parentWidth - sum < 0 || sum > gridWidth) {
            sizesForColumns = colSizes.avgColSizes;
        }

        this.clearColgroups();
        
        let totalWidth = 0;
        let finalWidth = 0;
        let titleWidth = 0;

        sizesForColumns.forEach((average, index) => {
            const column = cols[index];
            let colTag = "";
            if (column) {
                if (baseTR) {
                    const column = cols[index];
                    let title = column.title ?? "";
                    if (column != null && this.grid.getHeader().isSortedBy(column)) {
                        title = title + "..."
                    }
                    
                    // Folgendes "Problem" gibt es:
                    // Das Grid bekommt beim laden 2 Requests durch die Subscriptions. Einmal für setData() und einmal für setColumns
                    // Beim ersten Request sind die Werte noch unbekannt bzw. noch nicht korrekt und meistens durch ein '-' dargestellt. Auf Basis dieses '-' wird die Spaltenbreite berechent was bspw. 4px sind
                    // Im Nachgang wird der Tabellenkopf berechnet welcher z.B. 60px hat -> 60px werden als Spaltenbreite genutzt. Dieser Wert wird in dem width-Property der Column hinterlegt.
                    // Kommt nun der 2 Request, so wird der vorher berechnete Wert, also die 60px ausgelesen. Hat sich in der Zeit der Wert verändert so wird eine falsche Breite abgerufen
                    // Somit darf hier nur ein fester-Wert genutzt werden, wenn dieser nicht autom. berechnet wurde. Ansonsten muss neu berechnet werden, da sich die Werte verändert haben können 
                    if (column.width && !column.widthAutoCalculated) {
                        finalWidth = Utilities.getWidthAsNumber(column.width);
                    }
                    else {
                        const autoCalculated = Utilities.getWidthAsNumber(ColumnWidthHelper.autoCalcWidth(this.grid, column, null, average, baseTR));
                        const headRowStyle = this.grid.getHeader().getStylingInformation();
                        titleWidth = ColumnWidthHelper.calculateByFontSize(headRowStyle.fontSize, headRowStyle.fontFamily, headRowStyle.fontWeight, title);
                        finalWidth = autoCalculated;
                        if (titleWidth > autoCalculated && average < autoCalculated) {
                            finalWidth = titleWidth;
                        }
    
                        column.width = finalWidth;
                    }

                    colTag = this.createColTag(column.width);
                    const calculated = Utilities.getWidthAsNumber(finalWidth)
                    totalWidth += calculated;
                    calculatedWidths.push(calculated);
                } else {
                    const title = column.title ?? "";
                    const headRowStyle = this.grid.getHeader().getStylingInformation();
                    titleWidth = ColumnWidthHelper.calculateByFontSize(headRowStyle.fontSize, headRowStyle.fontFamily, headRowStyle.fontWeight, title);
                    titleWidth += ColumnWidthHelper.getPaddings(this.grid);

                    if (column.width) {
                        const baseWidth = Utilities.getWidthAsNumber(column.width);
                        finalWidth = (baseWidth > titleWidth ? baseWidth : titleWidth);
                        colTag = this.createColTag(Utilities.getWidthAsNumber(finalWidth));
                    } else {
                        finalWidth = Utilities.getWidthAsNumber(titleWidth);
                        colTag = this.createColTag(Utilities.getWidthAsNumber(titleWidth));
                    }

                    const calculated = Utilities.getWidthAsNumber(finalWidth)
                    calculatedWidths.push(calculated);
                    totalWidth += Utilities.getWidthAsNumber(finalWidth);
                }

                ColumnWidthHelper.addOrRemoveFittedClassForHeader(this.grid, index, column, Utilities.isLargerOrEqual(finalWidth, titleWidth));
                ColumnWidthHelper.addOrRemoveFittedClassForBody(this.grid, index, column, Utilities.isLargerOrEqual(finalWidth, Utilities.getWidthAsNumber(average)));
                this.addColToColgroup(colTag);
            }
        });

        if (!(totalWidth == 100)) {
            this.grid.useFillColumn = true;
        } else {
            this.grid.useFillColumn = false;
            this.grid.getBody().$tbody.find('.' + Constants.CLASS_CELL_FILLCOLUMN).remove();
        }

        if (this.grid.useFillColumn) {
            const fillCell = document.createElement('td');
            fillCell.classList.add(Constants.CLASS_CELL_FILLCOLUMN);
            for (let i = 0; i < trs.length; i++) {
                if (!this.containsNodeWithClass(trs[i], Constants.CLASS_CELL_FILLCOLUMN)) {
                    trs[i].appendChild(fillCell.cloneNode());
                }
            }

            const col = '<col style="width:auto">';
            this.addColToColgroup(col);
        }

        this.grid.getHeader().$colgroup.append(this.createColTag(this.grid.getBody().getScrollbarThickness()));
        if(this.grid.getOptions().hasTotalRow)
            this.grid.getTotalRow().$colgroup.append(this.createColTag(this.grid.getBody().getScrollbarThickness()));
        
        return trs;
    }

    private finalizeBodyTable(rows: Array<any>, cols: Array<IHeaderCol>, cellValueSizes: number[][], calculatedWidths: number[]) {
        return this.makeTableRows(rows, cols, cellValueSizes, calculatedWidths);
    }

    private getMaxAndAverage(arr: number[][], cols: Array<IHeaderCol>): IColSizes {
        const maxSizes = [];
        const avgSizes = [];

        const stylingInformation = this.grid.getHeader().getStylingInformation();

        for (let i = 0; i < arr.length; i++) {
            const curColumn = cols[i];
            const headerWidth = ColumnWidthHelper.calculateByFontSize(stylingInformation.fontSize, stylingInformation.fontFamily, stylingInformation.fontWeight, curColumn.title);

            let max = Utilities.max(arr[i]);
            let avg = Utilities.average(arr[i]);

            // Spalte hat keine Eintraege, oder nur ein paar welche kleiner sind von der Länge als der Header -> Nehme die Breite des dargestellten Headers für die spätere Berechnung
            if (max == 0 || max < headerWidth)
                max = headerWidth

            if (avg == 0 || avg < headerWidth)
                avg = headerWidth;

            maxSizes.push(max);
            avgSizes.push(avg);
        }
           
        const ret: IColSizes = {
            maxColSizes: maxSizes,
            avgColSizes: avgSizes
        }

        return ret;
    }

    private containsNodeWithClass(node: HTMLElement, htmlClass: string): boolean {
        const childNodes = node.childNodes;
        let found = false;
        for (let i = 0; i < childNodes.length; i++) {
            const child = childNodes[i] as HTMLElement;
            child.classList.forEach((v, k) => {
                if (v === Constants.CLASS_CELL_FILLCOLUMN) {
                    found = true;
                    return;
                }
            });

            if (found)
                break;
        }

        return found;
    }

    public fillBodySync(rows: Array<HTMLTableRowElement>) {
        const tbodyFragment = document.createDocumentFragment();
        rows.forEach(tr => tbodyFragment.appendChild(tr))
        this.tbody.appendChild(tbodyFragment);
    }

    public async fillBodyAsync(rows: Array<HTMLTableRowElement>): Promise<void> {
        return new Promise<void>(async (resolve) => {
            const trs = rows;
            let chunkBaseSize: number;
            if (trs.length === 0) {
                chunkBaseSize = 1;
            } else if (trs.length < 50) {
                chunkBaseSize = trs.length;
            } else {
                chunkBaseSize = 50;
            }
        
            const promiseCollection: Array<Promise<DocumentFragment>> = [];
            const chunkSize = trs.length / chunkBaseSize;
        
            for (let i = 0; i < trs.length; i += chunkSize) {
                const htmlElementChunk = trs.slice(i, i + chunkSize);
        
                const htmlCollection = [];
                for (let i = 0; i < htmlElementChunk.length; i++) {
                    htmlCollection.push(htmlElementChunk[i]);
                }
        
                const promise = this.asyncInnerHTML(htmlCollection);
                
                promiseCollection.push(promise);
            }

            let renderingChunk: number;
            if (promiseCollection.length === 0) {
                renderingChunk = 1;
            } else if (promiseCollection.length < 25) {
                renderingChunk  = promiseCollection.length;
            } else {
                renderingChunk  = 25;
            }

            for (let i = 0; i < promiseCollection.length; i += renderingChunk)
                await this.renderChunk(promiseCollection.slice(i, i + renderingChunk));

            resolve();
        });
    }



    private async renderChunk(toRender: Promise<DocumentFragment>[]) {
        const tbodyFragment = document.createDocumentFragment();

        for (let y = 0; y < toRender.length; y++) {
            const finalFragment = await toRender[y];
            const presentHTML = this.tbody.getElementsByTagName('tr');
            
            if (presentHTML.length == 0) {
                tbodyFragment.appendChild(finalFragment);
            }

            if (finalFragment.hasChildNodes()) {
                tbodyFragment.appendChild(finalFragment);
            }
        }
    
        this.tbody.appendChild(tbodyFragment);
    }

    private clearColgroups(): void{
        this.grid.getBody().$colgroup.empty();
        this.grid.getHeader().$colgroup.empty();
        if(this.grid.getOptions().hasTotalRow){
            this.grid.getTotalRow().$colgroup.empty();
        }
    }

    private addColToColgroup(col: string): void{
        this.grid.getBody().$colgroup.append(col);
        this.grid.getHeader().$colgroup.append(col);
        if(this.grid.getOptions().hasTotalRow){
            this.grid.getTotalRow().$colgroup.append(col);
        }
    }

    public async enterEditModeFor(row: HTMLTableRowElement, scrollToELement: boolean): Promise<HTMLTableRowElement> {
        const newRow = await this.changeEditModeFor(row, true);

        if (scrollToELement)
            this.$wrapper[0].scrollTop = newRow.offsetTop;

        return newRow;
    }

    public leaveEditModeFor(row: HTMLTableRowElement) {
        this.changeEditModeFor(row, false);
    }

    private async changeEditModeFor(row: HTMLTableRowElement, editMode: boolean): Promise<HTMLTableRowElement> {
        if (row) {
            if (!jQuery.contains(document.documentElement, row)) {
                this.grid.resetWrapperClasses();
                return row;
            }
        }

        const rowData = this.grid.getRowData(row.dataset[Constants.DATA_ATTRIBUTE_ROW_ID]);
        const index = this.grid.dataRows.indexOf(rowData);
        const newEditRow = await this.createDataRowAsync(rowData, index, editMode, [[]])

        row.replaceWith(newEditRow);
        row.remove();
        this.grid.resetWrapperClasses();

        this.grid.onEditModeChanged(newEditRow, editMode);

        return newEditRow;
    }

    private async createMultipleDataRowsAsync(rows, index: number, editMode: boolean = false, sizes: number[][], chunkSize: number): Promise<Array<HTMLTableRowElement>> {
        return new Promise<Array<HTMLTableRowElement>>((resolve, reject) => {
            setTimeout(() => { 
                let dataRows = [];
                let chunkCounter = 0;
                for (let i = index; (chunkCounter != chunkSize && i < rows.length); i++) {
                    const row = rows[i];
                    if (row) {
                        const tr = this.buildDataRow(row, i, editMode, sizes);
                        dataRows.push(tr);
                    }
                    chunkCounter++;
                } 
                
                resolve(dataRows);
            });
        });
    }

    private async createDataRowAsync(row, index: number, editMode: boolean = false, sizes: number[][]): Promise<HTMLTableRowElement> {
        return new Promise<HTMLTableRowElement>((resolve, reject) => {
            setTimeout(() => { 
                const tr = this.buildDataRow(row, index, editMode, sizes);
                resolve(tr);
            });
        });
    }

    private createDataRowSync(row, index: number, editMode: boolean = false, sizes: number[][]): HTMLTableRowElement {
        const tr = this.buildDataRow(row, index, editMode, sizes);
        return tr;
    }

    private buildDataRow(row, index: number, editMode: boolean = false, sizes: number[][]): HTMLTableRowElement  {
        const tr = this.createTableRowElement(editMode);
        const cellValues = [];

        const cols = this.grid.getAllVisibleLeafColumns();

        const sizeForRow: number[] = [];
        for (let colIndex = 0; colIndex < cols.length; colIndex++) {
            const currentCol = cols[colIndex];

            const raw = currentCol.value || currentCol.field;
            const uiGenerator = this.resolveUiGenerator(currentCol.uiGenerator);
            const editor = this.resolveUiGenerator(currentCol.editor);

            const td = this.createTableCellElement();
            td.dataset[Constants.DATA_ATTRIBUTE_SHOWS_TOOLTIP] = currentCol.showsTooltip ? 'true' : 'false';

            if (currentCol.isEditColumn) {
                if (editMode) {
                    const saveButton = this.createSaveButtonElement();
                    td.append(saveButton);
                }
                else {
                    const editButton = this.createEditButtonElement();
                    td.append(editButton);
                }

                const deleteButton = this.createDeleteButtonElement();
                td.append(deleteButton);

                sizeForRow.push(70);
            } else {
                let cellContent: HTMLElement | string | JQuery | Promise<any>;

                if (editMode) {
                    if (typeof editor == "function")
                        cellContent = editor(raw, row);
                    else
                        cellContent = uiGenerator(raw, row);
                }
                else
                    cellContent = uiGenerator(raw, row);
                
                if (cellContent && this.isPromise(cellContent)) {
                    cellContent.then((value: HTMLElement | JQuery | string) => {
                        this.appendContentToTableCell(td, value);
                    });
                } else {
                    if (cellContent == null) {
                        td.dataset[Constants.DATA_ATTRIBUTE_IS_NULL] = true.toString();
                    } else if (typeof cellContent === 'object') {
                        const obj = cellContent;
                        if (obj.length > 0) {
                            for (let i = 0; i < obj.length; i++) {
                                this.appendContentToTableCell(td, obj[i]);
                            }
                        } else {
                            this.appendContentToTableCell(td, cellContent);
                        }
                    } else {
                        // handle a concrete value
                        td.innerHTML = cellContent;
                    }
                }

                cellValues.push(cellContent);
                const headRowStyle = this.grid.getHeader().getStylingInformation();
                const size = ColumnWidthHelper.calculateByFontSize(headRowStyle.fontSize, headRowStyle.fontFamily, headRowStyle.fontWeight, cellContent);
                sizeForRow.push(size);
            }

            // Klasse der Zelle setzen
            if (currentCol.dataClass)
                td.classList.add(currentCol.dataClass);

            td.dataset[Constants.DATA_ATTRIBUTE_COLUMN] = currentCol.name;

            if (this.grid.useLocator())
                td.dataset[Constants.DATA_ATTRIBUTE_LOCATOR] = `${this.options.locator}.row.${index}.col.[${currentCol.name}]`;

            if(Utilities.isFunction(currentCol.onAfterCellRender))
                currentCol.onAfterCellRender($(td), raw, row, index)

            tr.appendChild(td);
        }
        sizes.push(sizeForRow);
        this.allCells.push(cellValues);
        
        if (this.grid.useLocator())
            tr.dataset[Constants.DATA_ATTRIBUTE_LOCATOR] = `${this.options.locator}.row.${index}`;

        if (this.grid.useFillColumn) {
            const fillCell = this.createFillCellElement();
            tr.appendChild(fillCell);
        }
        
        const rowIdName = row[this.options.rowIdName]
        if (rowIdName) { // prevent an 'undefined' attribute-value in HTML-DOM + undefined data in storage
            tr.dataset[Constants.DATA_ATTRIBUTE_ROW_ID] = rowIdName;
            this.grid.storeRowData(rowIdName?.toString(), row);
        }

        return tr;
    }
    
    private isPromise(v: unknown): v is Promise<unknown> {
        return typeof (v as any)?.then === 'function';
    }
    
    private appendContentToTableCell(td: HTMLTableCellElement, value: HTMLElement | JQuery | string) {
        if (value == null) {
            td.dataset[Constants.DATA_ATTRIBUTE_IS_NULL] = true.toString();
            return;
        }
        
        if (value instanceof jQuery) {
            const castedValue = value as JQuery;
            if (castedValue.length === 0) {
                td.innerText = '';
                return;
            }
            
            td.appendChild(castedValue[0]);
            return;
        } else if (value instanceof HTMLElement) {
            td.appendChild(value);
            return;
        } else if (typeof value === "string") {
            td.innerText = value;
            return;
        } else {
            td.innerText = '';
            console.warn('Object cannot be appended as a table cell: ' + JSON.stringify(value))
            return;
        }
    }

    private createTableRowElement(editMode: boolean): HTMLTableRowElement {
        const templateElement = this.options.elementTemplates.tableRow;
        const element = templateElement ? this.clone(templateElement) : document.createElement('tr');
        element.classList.add(Constants.CLASS_ROW);
        element.setAttribute('role', 'row');

        if (editMode) {
            element.classList.add(Constants.CLASS_ROW_IN_EDIT);
        }

        return element;
    }

    private createTableCellElement(): HTMLTableCellElement {
        const templateElement = this.options.elementTemplates.tableCell;
        const element = templateElement ? this.clone(templateElement) : document.createElement('td');
        element.setAttribute('role', Constants.ROLE_CELL);
        return element;
    }

    private createFillCellElement(): HTMLTableCellElement {
        const templateElement = this.options.elementTemplates.fillCell;
        const element = templateElement ? this.clone(templateElement) : document.createElement('td');
        element.classList.add(Constants.CLASS_CELL_FILLCOLUMN);
        return element;
    }

    private createEditButtonElement(): HTMLElement {
        const templateElement = this.options.elementTemplates.editButton;
        const element = templateElement ? this.clone(templateElement) : document.createElement('div');
        element.classList.add(Constants.CLASS_ROW_BUTTON, Constants.CLASS_ROW_EDITBUTTON);
        return element;
    }

    private createSaveButtonElement(): HTMLElement {
        const templateElement = this.options.elementTemplates.saveButton;
        const element = templateElement ? this.clone(templateElement) : document.createElement('div');
        element.classList.add(Constants.CLASS_ROW_BUTTON, Constants.CLASS_ROW_SAVEBUTTON);
        return element;
    }

    private createDeleteButtonElement(): HTMLElement {
        const templateElement = this.options.elementTemplates.deleteButton;
        const element = templateElement ? this.clone(templateElement) : document.createElement('div');
        element.classList.add(Constants.CLASS_ROW_BUTTON, Constants.CLASS_ROW_DELETEBUTTON);
        return element;
    }

    private resolveUiGenerator(uiGenerator: string | Function): generatorFunction {
        if (typeof uiGenerator == "function") {
            return (cell, row) => {
                // Wenn cell eine Funktion ist, rufe sie mit der Row als Parameter auf
                if (typeof cell == "function")
                    return uiGenerator(cell(row), row)
                // Anderenfalls ist cell ein Feldname der Row, greife also den Wert aus der Row (per Index) ab
                else
                    return uiGenerator(row[cell], row);
            }
        }

        return (cell, row) => {
            if (typeof cell == "function")
                return cell(row);
            else if(typeof row[cell] =="string"){
                return Utilities.escape(row[cell]);
            }
            else
                return row[cell];
        }
    }

    public getTableRowsByIds(rowIds: Array<string | number>): Array<HTMLTableRowElement> {
        rowIds = rowIds.filter((e) => { return e != undefined; });
        let result: Array<HTMLTableRowElement> = [];

        const trs = this.tbody.getElementsByTagName('tr');
        for (let i = 0; i < trs.length; i++) {
            const tr = trs[i]; 

            if (tr.dataset?.rowid != null) {
                let currentRowId = tr.dataset[Constants.DATA_ATTRIBUTE_ROW_ID];
                const matching = rowIds.filter((r) => {
                    if (r + '' == currentRowId + '') {
                        return true;
                    } 
                });

                if (matching.length == 1)
                    result.push(tr);
            }
        }

        return result;
    }

    public getAllValuesForColumn(column: IHeaderCol) {
        const visibleColumns = this.grid.getAllVisibleLeafColumns().filter(x => !x.isEditColumn);
        const columnIndex = visibleColumns.indexOf(column);
        const values = [];
        const limit = this.allCells.length > 100 ? 100 : this.allCells.length;
        const valuesToIterate = limit == 100 ? this.allCells.slice(0, limit) : this.allCells;
        
        valuesToIterate.forEach((cellValues: any[]) => {
            const value = cellValues[columnIndex];
            if (typeof (value) == 'string') { 
                values.push(this.htmlDecodeWithRegex(value)) }
            else {
                values.push(value);
            }
        });

        return values;
    }

    private htmlDecodeWithRegex(value: string): any {
        const pattern = /<(?:"[^"]*"['"]*|'[^']*'['"]*|[^'">])+>/g;
        const matches = pattern.exec(value);

        if (matches == null)
            return value;

        const body = matches[0];
        
        return body;
    }

    private extractColumnSizesOfIndexForRow(rows: number[], index: number): number[] {
        const result: number[] = [];
        rows.forEach((value, index, array) => {
            result.push(array[index]);
        });


        return result;
    }

    private async asyncInnerHTML(html: Array<HTMLElement>): Promise<DocumentFragment> {
        return new Promise((resolve) => {
            const temp = document.createElement('tbody');
            const fragment = document.createDocumentFragment();

            temp.setAttribute('role', 'rowgroup');
            temp.setAttribute('tabindex', '-1');

            html.forEach((elem) => {
                temp.appendChild(elem);
            });

            const insertHtml = (() => {
                if (temp.firstChild) {
                    fragment.appendChild(temp.firstChild);
                    setTimeout(insertHtml, 0);
                } else {;
                    resolve(fragment);
                }
            });

            insertHtml();
        });
    }

    private createColTag(finalWidth: number | string) : string {
        let col: string = '<col style="width:' + finalWidth;
        if (!isNaN(finalWidth as number)) {
            col += 'px">'
        } else {
            col += '">'
        }

        return col;
    }
}
